import React from 'react';
import { SprintEntity, useListSprints } from '@data';

/** Earlier years have no rates or budgets on record, so their money would only be made up. */
export const FIRST_MONEY_YEAR = '2026';

/** Why a tab has no year to show: no sprints at all, or only ones from before the cutoff. */
export const noSprintsKey = (sprints: SprintEntity[]): string =>
  sprints.length ? 'budget.noSprintsSinceCutoff' : 'budget.noSprints';

/** Paged by year: the sprints are still the unit, but which one you are on is no longer a question. */
/** A chosen year held above the hook, so readings that swap in and out keep the same one. */
export type SharedYear = [string | null, (year: string) => void];

export const useYearChoice = ({
  nextYear = false,
  shared,
  opensOn,
}: {
  nextYear?: boolean;
  shared?: SharedYear;
  /** A sprint the page was opened on, whose year is the default until another is chosen. */
  opensOn?: string;
} = {}): {
  /** Every sprint the organization has, whatever year it falls in. */
  sprints: SprintEntity[];
  /** The years with sprints in them from FIRST_MONEY_YEAR to this year, or next with `nextYear`, newest first. */
  years: string[];
  year: string;
  choose: (year: string) => void;
  /** The chosen year's sprints, oldest first: the workforce grid's columns. */
  yearSprints: SprintEntity[];
  isLoading: boolean;
} => {
  const { data, isLoading } = useListSprints({ limit: 400 });
  const [own, setOwn] = React.useState<string | null>(null);
  const [held, setChosen] = shared ?? [own, setOwn];

  const sprints = React.useMemo(() => data?.items ?? [], [data]);
  const thisYear = new Date().getFullYear().toString();
  // Next year's sprints exist ahead of time so it can be budgeted; only Budget pages to it.
  const lastYear = nextYear ? String(Number(thisYear) + 1) : thisYear;
  const years = React.useMemo(
    () =>
      [...new Set(sprints.map(sprint => sprint.startDate.slice(0, 4)))]
        .filter(each => each >= FIRST_MONEY_YEAR && each <= lastYear)
        .sort()
        .reverse(),
    [sprints, lastYear],
  );

  // Only Budget pages to next year, so another reading falls back from it to its own default.
  const chosen = held !== null && years.includes(held) ? held : null;
  // The year being worked in, which is this one if it has sprints and the latest one otherwise.
  const openedYear = sprints.find(sprint => sprint.id === opensOn)?.startDate.slice(0, 4);
  const fallback = years.includes(thisYear) ? thisYear : (years[0] ?? thisYear);
  const year = chosen ?? (openedYear && years.includes(openedYear) ? openedYear : fallback);

  const yearSprints = React.useMemo(
    () => sprints.filter(sprint => sprint.startDate.slice(0, 4) === year).sort((a, b) => a.number - b.number),
    [sprints, year],
  );

  return { sprints, years, year, choose: setChosen, yearSprints, isLoading };
};
